import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import * as React from "react";
import {
  canonicalizeEntryValue,
  formatMessageId,
  inputModeForFormat,
  validateEntryValue,
  type FieldFormat,
} from "@kmvs/forms-core";
import { BRAND, REFINED } from "./tokens";
import { TelefonEingabe } from "../forms/widgets/TelefonEingabe";

const useStyles = makeStyles({
  /**
   * Umschließt Label + Eingabe + Meldung. Die Meldung darf NICHT ins `<label>`
   * — sie zählte dort zum zugänglichen Namen des Feldes, und der Screenreader
   * nennte das Feld „Telefon (privat) Bitte eine gültige Telefonnummer
   * eingeben". Angebunden wird sie über `aria-describedby`.
   */
  wrap: {
    display: "block",
    minWidth: 0,
  },
  label: {
    display: "block",
    minWidth: 0,
  },
  labelText: {
    display: "block",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.7rem",
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#5d5664",
    marginBottom: "3px",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    minWidth: 0,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    ...shorthands.padding("8px", "10px"),
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    transitionProperty: "border-color, box-shadow",
    transitionDuration: "120ms",
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: `0 0 0 3px ${BRAND.purple}33`,
    },
  },
  /** Formatverstoß mit `severity: "error"` — formal prüfbar und eindeutig falsch. */
  inputInvalid: {
    ...shorthands.borderColor(REFINED.err),
    backgroundColor: REFINED.errBg,
    ":focus": {
      ...shorthands.borderColor(REFINED.err),
      boxShadow: REFINED.errRing,
    },
  },
  /** Formatverstoß mit `severity: "warning"` — angemerkt, nicht als Fehler markiert. */
  inputWarn: {
    ...shorthands.borderColor(REFINED.gold),
    backgroundColor: REFINED.goldBg,
    ":focus": {
      ...shorthands.borderColor(REFINED.gold),
      boxShadow: `0 0 0 3px ${REFINED.gold}22`,
    },
  },
  message: {
    display: "block",
    marginTop: "4px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.76rem",
    lineHeight: 1.35,
  },
  messageError: {
    color: REFINED.err,
  },
  messageWarn: {
    color: REFINED.goldInk,
  },
});

type Props = {
  label: string;
  /**
   * Flacher Answer-Key der Liste (`gesellschafter`) — die Zuordnung, die auch
   * der Server kennt. Der Pfad sagt zusätzlich, welcher Eintrag gemeint ist.
   */
  listKey: string;
  /** Voller Pfad des Teilfeldes, z. B. `gesellschafter[#3f9a…].kontakt.mobilPrivat`. */
  pfad: string;
  /** Ohne Format wird nichts geprüft und nichts kanonisiert. */
  format?: FieldFormat;
  value: string;
  placeholder?: string;
  onChange: (next: string) => void;
  /** Zusätzliche Beschreibung, z. B. der Summenhinweis über alle Anteile. */
  describedBy?: string;
  /** Meldet Fokus und Verlassen — für Hinweise, die zur ganzen Liste gehören. */
  onFokusWechsel?: (fokussiert: boolean) => void;
  className?: string;
};

/**
 * Ein Texteingabefeld INNERHALB eines Listeneintrags (Kontaktdaten, Firmen-
 * daten einer Verantwortlichkeit, Anteile) — mit derselben Prüf-Choreografie,
 * die QuestionField für flache Felder etabliert hat:
 *
 *  - gemeldet wird **beim Verlassen**, nicht beim Tippen,
 *  - kanonisiert wird **nur, was gültig ist**; Bemängeltes bleibt stehen,
 *  - Fehler mit `role="alert"` und `aria-invalid`, Warnung ohne beides,
 *  - die Meldung steht außerhalb des `<label>` und hängt per
 *    `aria-describedby` am Feld.
 *
 * Die Meldung erscheint dadurch **an dem Eintrag**, zu dem sie gehört, statt
 * gesammelt über der Liste.
 *
 * Bewusst immer `type="text"` mit `inputMode`, nie `type="tel" | "email" |
 * "number"`: die native Constraint-Validierung brächte eine zweite, nicht
 * beeinflussbare (und englische) Meldung ins Spiel und könnte ein Abschicken
 * blockieren — hier soll nichts blockieren. `type="number"` verwirft
 * außerdem „1.234,56" schon beim Tippen. Die passende Handytastatur kommt
 * über `inputMode` aus der Format-Registry.
 */
const EntryField = ({
  label,
  listKey,
  pfad,
  format,
  value,
  placeholder,
  onChange,
  describedBy,
  onFokusWechsel,
  className,
}: Props) => {
  const styles = useStyles();
  // Erst nach dem Verlassen wird gemeldet — sonst schreit jede halb getippte
  // Telefonnummer den Nutzer an, während er sie eingibt.
  const [beruehrt, setBeruehrt] = React.useState(false);
  const verstoss = beruehrt
    ? validateEntryValue(listKey, pfad, format, value)
    : undefined;
  const istFehler = verstoss?.severity === "error";
  const meldungId = verstoss ? formatMessageId(pfad) : undefined;
  const beschreibung =
    [meldungId, describedBy].filter(Boolean).join(" ") || undefined;

  const beimVerlassen = (roh: string) => {
    setBeruehrt(true);
    onFokusWechsel?.(false);
    const kanonisch = canonicalizeEntryValue(format, roh);
    if (kanonisch !== undefined) onChange(kanonisch);
  };

  return (
    <div className={mergeClasses(styles.wrap, className)}>
      <label className={styles.label}>
        <span className={styles.labelText}>{label}</span>
        {format === "phone" ? (
          // Telefon: einheitliches Format „+49 221 123456-78" beim Tippen;
          // Durchwahl nur bei geschäftlichen Nummern (nicht „…Privat").
          <TelefonEingabe
            className={mergeClasses(
              styles.input,
              istFehler ? styles.inputInvalid : undefined,
              verstoss && !istFehler ? styles.inputWarn : undefined,
            )}
            value={value}
            placeholder={placeholder}
            geschaeftlich={!/privat/i.test(String(pfad))}
            aria-invalid={istFehler || undefined}
            aria-describedby={beschreibung}
            onChange={onChange}
            onFocus={() => onFokusWechsel?.(true)}
            onBlur={(e) => beimVerlassen(e.target.value)}
          />
        ) : (
          <input
            type="text"
            inputMode={inputModeForFormat(format)}
            className={mergeClasses(
              styles.input,
              istFehler ? styles.inputInvalid : undefined,
              verstoss && !istFehler ? styles.inputWarn : undefined,
            )}
            value={value}
            placeholder={placeholder}
            aria-invalid={istFehler || undefined}
            aria-describedby={beschreibung}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => onFokusWechsel?.(true)}
            onBlur={(e) => beimVerlassen(e.target.value)}
          />
        )}
      </label>
      {verstoss && (
        <span
          id={meldungId}
          role={istFehler ? "alert" : undefined}
          className={mergeClasses(
            styles.message,
            istFehler ? styles.messageError : styles.messageWarn,
          )}
        >
          {verstoss.message}
        </span>
      )}
    </div>
  );
};

export default EntryField;
